import type { Locale } from "./i18n.ts";
import { generatedCommunityText, generatedCommunityTranslationsAreComplete, type GeneratedCommunityTextKey } from "./strategy-community-generated-i18n.ts";

const en = {
  navigationTitle: "Community",
  eyebrow: "Strategy community",
  title: "Explore strategies",
  subtitle: "Explore published backtest recipes, review their evidence, then rerun the same configuration with your own assumptions.",
  riskNotice: "Historical simulations are not forecasts. Configuration attestation checks reproducibility only; it does not verify profit or guarantee future performance.",
  noLiveTrading: "Simulation only · no exchange connection · no automatic orders",
  recommended: "Recommended",
  verified: "Reproducible",
  newest: "New",
  saved: "Saved",
  search: "Search strategies or markets",
  market: "Market",
  allMarkets: "All markets",
  timeframe: "Timeframe",
  allTimeframes: "All timeframes",
  evidence: "Evidence",
  allEvidence: "All evidence",
  evidenceAttested: "Configuration attested",
  evidenceHoldout: "Holdout evaluated",
  evidenceUnvalidated: "Unvalidated",
  drawdown: "Maximum drawdown",
  anyDrawdown: "Any drawdown",
  applyFilters: "Apply filters",
  clearFilters: "Clear",
  refreshHistory: "Refresh", myPublications: "Shared methods", historyEmpty: "You have not shared a method yet.", historyError: "Could not load your shared methods.", sourceLoading: "Loading other saved sources…", sourceLoadError: "Other sources could not be loaded. You can use a saved result or retry.", requestTimeout: "This is taking longer than expected. Check your shared methods or retry the same request.", localResult: "Saved backtest", remoteResult: "Shared source", openBacktest: "Open Backtest", shareShort: "Share to Community", shareHint: "Publish this tested method and simulated result. Historical profit does not predict future profit.", shareDetails: "What will be published?", openShared: "View shared methods", statusListed: "Published", statusPending: "Being checked · not public", statusArchived: "Archived", statusHidden: "Hidden", statusExpired: "Expired",
  publish: "Publish a method",
  loading: "Loading community methods…",
  unavailableTitle: "Community methods are not available yet",
  unavailableBody: "The server has not enabled strategy_community_v1. Your private research and shared links are unchanged.",
  retry: "Try again",
  loadError: "Community methods could not be loaded. Please try again.",
  emptyTitle: "No methods match these filters",
  emptyBody: "Adjust the filters or return later after more reproducible methods are published.",
  signInSaved: "Sign in to view saved methods.",
  signInAction: "Sign in",
  configurationAttested: "Configuration attested",
  reproducible: "Can be rerun",
  outOfSample: "Holdout evaluated",
  unvalidated: "Unvalidated",
  netReturn: "Net return",
  maxDrawdown: "Max drawdown",
  trades: "Trades",
  testedPeriod: "Tested period",
  independentReruns: "Completed reruns",
  simulate: "Review simulation",
  duplicate: "Copy & backtest",
  preparePaper: "Prepare for paper",
  favorite: "Save",
  unfavorite: "Saved",
  openDetail: "Open method details",
  loadMore: "Load more",
  backToCommunity: "Back to community",
  byCreator: "by {creator}",
  version: "Version {version}",
  overview: "Overview",
  rules: "Rules",
  evidenceTab: "Evidence",
  tradesTab: "Trades",
  versions: "Versions",
  evidenceHeading: "What this evidence establishes",
  attestationExplanation: "The server verified that the published recipe matches an immutable reproducible result. It did not verify profitability.",
  riskNote: "Risk note",
  assumptions: "Simulation assumptions",
  sharpe: "Sharpe ratio",
  winRate: "Win rate",
  profitFactor: "Profit factor",
  published: "Published",
  noRules: "No public rule summary is available for this version.",
  noTrades: "Community v1 does not publish individual trade history.",
  noVersions: "No earlier public version is available.",
  entry: "Entry",
  exit: "Exit",
  result: "Result",
  fees: "Fees",
  copiedReady: "The exact recipe is ready in Backtest. Review every assumption before running it.",
  duplicateUnavailable: "This version does not include a complete reproducible recipe.",
  actionError: "The action could not be completed. Please try again.",
  report: "Report",
  blockCreator: "Block this creator",
  blocked: "Creator blocked for your account.",
  reportTitle: "Report this publication",
  reportReason: "Reason",
  reportDetails: "Additional details",
  reportMisleading: "Misleading performance claim",
  reportSpam: "Spam or promotion",
  reportAbuse: "Abusive or unsafe content",
  reportCopyright: "Copyright or impersonation",
  submitReport: "Submit report",
  cancel: "Cancel",
  reported: "Report submitted. Thank you for helping keep the community useful.",
  publishTitle: "Publish a reproducible method",
  publishIntro: "Choose one of your reproducible shared results. Publication creates a separate, versioned community record.",
  noShares: "No eligible reproducible result is available. Create a reproducible share from a completed Visual Strategy Builder result first.",
  chooseShare: "Reproducible result",
  methodTitle: "Method title",
  summary: "What is the hypothesis?",
  creatorAlias: "Public alias",
  tags: "Tags",
  tagsHint: "trend, btc, conservative",
  publishSubmit: "Submit for publication",
  publishedSuccess: "Submitted for review. It will appear publicly only after moderation; its historical result remains a simulation, not a promise.",
  publishError: "This result could not be published. Check its eligibility and try again.",
  signInPublish: "Sign in to publish a reproducible result.",
  filtersLabel: "Community filters",
  sparklineLabel: "Historical simulated return curve",
  perPageStatus: "Showing {count} methods",
  stateInsufficient: "Insufficient evidence",
  stateHoldoutPassed: "Holdout passed",
  stateHoldoutFailed: "Holdout failed",
  stateInSample: "In-sample only",
} as const;

type LegacyCommunityTextKey = keyof typeof en;
type CommunityTextKey = LegacyCommunityTextKey | GeneratedCommunityTextKey;
type CommunityText = Record<LegacyCommunityTextKey, string>;

const vi: CommunityText = {
  navigationTitle: "Cộng đồng",
  eyebrow: "Cộng đồng phương pháp",
  title: "Khám phá chiến lược",
  subtitle: "Khám phá công thức backtest đã công bố, xem bằng chứng rồi tự chạy lại cùng cấu hình với giả định của bạn.",
  riskNotice: "Mô phỏng lịch sử không phải dự báo. Xác minh cấu hình chỉ kiểm tra khả năng tái lập; không xác minh lợi nhuận và không đảm bảo kết quả tương lai.",
  noLiveTrading: "Chỉ mô phỏng · không kết nối sàn · không tự động đặt lệnh",
  recommended: "Đề xuất",
  verified: "Có thể tái lập",
  newest: "Mới",
  saved: "Đã lưu",
  search: "Tìm phương pháp hoặc thị trường",
  market: "Thị trường",
  allMarkets: "Tất cả thị trường",
  timeframe: "Khung thời gian",
  allTimeframes: "Tất cả khung",
  evidence: "Bằng chứng",
  allEvidence: "Mọi mức bằng chứng",
  evidenceAttested: "Cấu hình đã xác minh",
  evidenceHoldout: "Đã đánh giá holdout",
  evidenceUnvalidated: "Chưa kiểm chứng",
  drawdown: "Drawdown tối đa",
  anyDrawdown: "Mọi mức drawdown",
  applyFilters: "Áp dụng bộ lọc",
  clearFilters: "Xóa lọc",
  refreshHistory: "Làm mới", myPublications: "Bài đã chia sẻ", historyEmpty: "Bạn chưa chia sẻ phương pháp nào.", historyError: "Chưa tải được bài đã chia sẻ.", sourceLoading: "Đang tải thêm nguồn đã lưu…", sourceLoadError: "Chưa tải được nguồn khác. Bạn vẫn có thể dùng kết quả đã lưu hoặc thử lại.", requestTimeout: "Yêu cầu đang mất nhiều thời gian. Xem bài đã chia sẻ hoặc thử lại cùng yêu cầu.", localResult: "Backtest đã lưu", remoteResult: "Nguồn chia sẻ", openBacktest: "Mở Backtest", shareShort: "Chia sẻ lên Cộng đồng", shareHint: "Chia sẻ phương pháp đã kiểm thử và kết quả mô phỏng. Lãi quá khứ không dự báo lãi tương lai.", shareDetails: "Thông tin sẽ công khai", openShared: "Xem bài đã chia sẻ", statusListed: "Đã đăng", statusPending: "Đang kiểm tra · chưa công khai", statusArchived: "Đã lưu trữ", statusHidden: "Đã ẩn", statusExpired: "Đã hết hạn",
  publish: "Đăng phương pháp",
  loading: "Đang tải phương pháp cộng đồng…",
  unavailableTitle: "Cộng đồng phương pháp chưa khả dụng",
  unavailableBody: "Máy chủ chưa bật strategy_community_v1. Nghiên cứu riêng tư và các link đã chia sẻ của bạn không bị thay đổi.",
  retry: "Thử lại",
  loadError: "Không thể tải phương pháp cộng đồng. Vui lòng thử lại.",
  emptyTitle: "Không có phương pháp phù hợp bộ lọc",
  emptyBody: "Hãy đổi bộ lọc hoặc quay lại khi có thêm phương pháp có thể tái lập.",
  signInSaved: "Đăng nhập để xem các phương pháp đã lưu.",
  signInAction: "Đăng nhập",
  configurationAttested: "Cấu hình đã xác minh",
  reproducible: "Có thể chạy lại",
  outOfSample: "Đã đánh giá holdout",
  unvalidated: "Chưa kiểm chứng",
  netReturn: "Lợi nhuận ròng",
  maxDrawdown: "Drawdown tối đa",
  trades: "Giao dịch",
  testedPeriod: "Giai đoạn kiểm thử",
  independentReruns: "Lượt chạy lại hoàn tất",
  simulate: "Xem mô phỏng",
  duplicate: "Sao chép & kiểm thử",
  preparePaper: "Chuẩn bị chạy paper",
  favorite: "Lưu",
  unfavorite: "Đã lưu",
  openDetail: "Mở chi tiết phương pháp",
  loadMore: "Tải thêm",
  backToCommunity: "Quay lại Cộng đồng",
  byCreator: "bởi {creator}",
  version: "Phiên bản {version}",
  overview: "Tổng quan",
  rules: "Luật",
  evidenceTab: "Bằng chứng",
  tradesTab: "Giao dịch",
  versions: "Phiên bản",
  evidenceHeading: "Bằng chứng này xác nhận điều gì",
  attestationExplanation: "Máy chủ xác nhận công thức được công bố khớp với một kết quả bất biến có thể tái lập. Máy chủ không xác minh lợi nhuận.",
  riskNote: "Lưu ý rủi ro",
  assumptions: "Giả định mô phỏng",
  sharpe: "Hệ số Sharpe",
  winRate: "Tỷ lệ thắng",
  profitFactor: "Profit factor",
  published: "Đăng ngày",
  noRules: "Phiên bản này chưa có tóm tắt luật công khai.",
  noTrades: "Cộng đồng v1 không công khai lịch sử từng giao dịch.",
  noVersions: "Chưa có phiên bản công khai trước đó.",
  entry: "Vào lệnh",
  exit: "Thoát lệnh",
  result: "Kết quả",
  fees: "Phí",
  copiedReady: "Công thức chính xác đã sẵn sàng trong Kiểm thử. Hãy kiểm tra mọi giả định trước khi chạy.",
  duplicateUnavailable: "Phiên bản này không có đủ công thức để tái lập.",
  actionError: "Không thể hoàn tất thao tác. Vui lòng thử lại.",
  report: "Báo cáo",
  blockCreator: "Chặn tác giả này",
  blocked: "Đã chặn tác giả trên tài khoản của bạn.",
  reportTitle: "Báo cáo bài đăng",
  reportReason: "Lý do",
  reportDetails: "Thông tin bổ sung",
  reportMisleading: "Tuyên bố hiệu suất gây hiểu nhầm",
  reportSpam: "Spam hoặc quảng cáo",
  reportAbuse: "Nội dung lạm dụng hoặc không an toàn",
  reportCopyright: "Bản quyền hoặc mạo danh",
  submitReport: "Gửi báo cáo",
  cancel: "Hủy",
  reported: "Đã gửi báo cáo. Cảm ơn bạn giúp cộng đồng an toàn hơn.",
  publishTitle: "Đăng phương pháp có thể tái lập",
  publishIntro: "Chọn một kết quả chia sẻ có thể tái lập. Bài đăng là một bản ghi cộng đồng có phiên bản riêng.",
  noShares: "Không có kết quả đủ điều kiện. Trước tiên, hãy tạo link có thể tái lập từ kết quả Visual Strategy Builder đã hoàn tất.",
  chooseShare: "Kết quả có thể tái lập",
  methodTitle: "Tên phương pháp",
  summary: "Giả thuyết của phương pháp là gì?",
  creatorAlias: "Bí danh công khai",
  tags: "Thẻ",
  tagsHint: "xu hướng, btc, thận trọng",
  publishSubmit: "Gửi đăng",
  publishedSuccess: "Đã gửi xét duyệt. Phương pháp chỉ xuất hiện công khai sau khi được kiểm duyệt; kết quả lịch sử vẫn chỉ là mô phỏng, không phải cam kết.",
  publishError: "Không thể đăng kết quả này. Hãy kiểm tra điều kiện rồi thử lại.",
  signInPublish: "Đăng nhập để đăng một kết quả có thể tái lập.",
  filtersLabel: "Bộ lọc cộng đồng",
  sparklineLabel: "Đường lợi nhuận mô phỏng lịch sử",
  perPageStatus: "Đang hiển thị {count} phương pháp",
  stateInsufficient: "Chưa đủ bằng chứng",
  stateHoldoutPassed: "Holdout đạt",
  stateHoldoutFailed: "Holdout không đạt",
  stateInSample: "Chỉ in-sample",
};

export function communityT(
  locale: Locale,
  key: CommunityTextKey,
  parameters: Record<string, string | number> = {},
): string {
  const source = locale === "vi" ? vi : en;
  return Object.entries(parameters).reduce(
    (value, [name, replacement]) => value.split(`{${name}}`).join(String(replacement)),
    generatedCommunityText(locale, key) || source[key as LegacyCommunityTextKey] || en[key as LegacyCommunityTextKey],
  );
}

export function communityTranslationsAreComplete(): boolean {
  return generatedCommunityTranslationsAreComplete()
    && (Object.keys(en) as LegacyCommunityTextKey[]).every((key) => Boolean(vi[key]?.trim()));
}
